import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { provideTranslocoScope, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SdsIcon } from '@nieuwestroom/sds-icon';
import { SdsButton } from '@nieuwestroom/sds-ui/button';
import { SdsCard, SdsCardAside, SdsCardBleedBlockEnd, SdsCardFooter } from '@nieuwestroom/sds-ui/card';
import { SdsDialog } from '@nieuwestroom/sds-ui/dialog';
import { NgpDialogManager } from 'ng-primitives/dialog';
import { NgpToastManager } from 'ng-primitives/toast';

import { CommunicationPreferences } from '@customer-portal/domains/customer-preferences/api';
import { NS_TOAST_INTENTS, NsToast, NsToastIntent } from '@customer-portal/indicators/toast';
import { ActiveMemberState } from '@customer-portal/shared/account/data-access';

import { OnboardingStatusService } from '../data-access';
import { OnboardingStepperStep, OnboardingTaskKey } from '../model';
import { NscpOnboardingStepper } from '../ui';
import { DownloadAppStep } from './download-app-step';
import { InviteManagerStep } from './invite-manager-step';

@Component({
  selector: 'nscp-onboarding-wizard',
  imports: [
    TranslocoDirective,
    SdsIcon,
    SdsButton,
    SdsDialog,
    SdsCard,
    SdsCardBleedBlockEnd,
    SdsCardAside,
    SdsCardFooter,
    NscpOnboardingStepper,
    CommunicationPreferences,
    InviteManagerStep,
    DownloadAppStep,
  ],
  providers: [provideTranslocoScope('onboarding')],
  templateUrl: './onboarding-wizard.html',
  styleUrl: './onboarding-wizard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'nscp-onboarding-wizard',
  },
})
export class OnboardingWizard implements OnDestroy {
  private readonly onboardingStatusService = inject(OnboardingStatusService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dialogManager = inject(NgpDialogManager);
  private readonly toastManager = inject(NgpToastManager);
  private readonly activeMemberState = inject(ActiveMemberState);

  protected readonly tasks = this.onboardingStatusService.tasks;

  // Optional: only present while the matching step is the one being rendered.
  private readonly communicationPreferencesStep = viewChild(CommunicationPreferences);
  private readonly inviteManagerStep = viewChild(InviteManagerStep);
  private readonly body = viewChild<ElementRef<HTMLElement>>('onboardingWizardBody');
  private readonly stepContent = viewChild<ElementRef<HTMLElement>>('stepContent');

  protected readonly isCompletingTask = computed(
    () => this.onboardingStatusService.isCompletingTask() || (this.communicationPreferencesStep()?.isSaving() ?? false) || (this.inviteManagerStep()?.isSaving() ?? false),
  );

  protected readonly maxStepContentHeight = signal(0);
  private stepContentResizeObserver: ResizeObserver | undefined;

  // To prevent the dialog from jumping when switching steps, tracking the tallest height seen so far
  // and applying it as a min-height on an inner wrapper (see template) keeps the dialog stable once
  // the tallest step has been visited once.
  private readonly onStepContentChange = effect(() => {
    const element = this.stepContent()?.nativeElement;

    if (element && !this.stepContentResizeObserver) {
      // offsetHeight (border-box, includes padding) rather than the observer's own contentRect -
      // min-height below is compared against the border-box height, so the two must use the same box.
      this.stepContentResizeObserver = this.observeElementResize(element, () => {
        // caps each candidate height to availableStepContentHeight() before recording it as the new max
        const candidate = Math.min(element.offsetHeight, this.availableStepContentHeight());
        this.maxStepContentHeight.update((max) => Math.max(max, candidate));
      });
    }
  });

  private availableStepContentHeight(): number {
    const bodyElement = this.body()?.nativeElement;
    // clientHeight is 0 both before the body has ever been laid out and in test environments
    // (jsdom doesn't do real layout) - treat that as "unknown" rather than clamping every
    // candidate height down to 0.
    if (!bodyElement || bodyElement.clientHeight === 0) {
      return Infinity;
    }

    const styles = getComputedStyle(bodyElement);
    const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    return bodyElement.clientHeight - verticalPadding;
  }

  protected readonly currentIndex = signal(0);

  // Tasks completed during this wizard instance. currentIndex is local UI state while completions
  // are permanent server-side, so navigating back past a completed step and asserting it again
  // would 409 (the backend only accepts completing the currently-active task) - this guard makes
  // re-completing a no-op advance instead.
  private readonly completedKeys = signal<ReadonlySet<OnboardingTaskKey>>(new Set());

  private hasInitializedCurrentIndex = false;

  // Resumes the wizard at whichever task the backend marks `isActive` (e.g. a user who already
  // completed the first step in an earlier session), instead of always restarting at step 0.
  // Only runs once - after that, currentIndex is purely local UI state advanced by onNext/onPrevious.
  private readonly onTasksLoaded = effect(() => {
    if (this.hasInitializedCurrentIndex || this.onboardingStatusService.resource.isLoading()) {
      return;
    }

    this.hasInitializedCurrentIndex = true;

    const tasks = this.tasks();
    const activeIndex = tasks.findIndex((task) => task.isActive);
    this.currentIndex.set(activeIndex === -1 ? tasks.length : activeIndex);
    this.completedKeys.set(new Set(tasks.filter((task) => task.isCompleted).map((task) => task.key)));
  });

  // Steps can be taller than the dialog body, which stays scrolled to wherever the user left it -
  // reset it to the top whenever the active step changes so a new step always starts in view.
  private readonly onStepChanged = effect(() => {
    this.currentIndex();
    this.body()?.nativeElement.scrollTo({ top: 0 });
  });

  // All actual steps completed, showing the last "Je account is gereed" step
  protected readonly isOnCompletedStep = computed(() => this.currentIndex() >= this.tasks().length);

  protected readonly currentTaskKey = computed<OnboardingTaskKey | undefined>(() => this.tasks()[this.currentIndex()]?.key);

  protected readonly currentStepIntro = computed(() => {
    const key = this.currentTaskKey();
    if (!key) {
      return '';
    }

    return this.translocoService.translate(`onboarding.steps.${key}.intro`, {
      companyName: this.activeMemberState.activeCustomerName() || this.translocoService.translate('onboarding.steps.inviteManager.introCompanyFallback'),
    });
  });

  // Optional fine-print shown below a step's content; most steps have no disclaimer key at all.
  protected readonly currentStepDisclaimer = computed(() => {
    const key = this.currentTaskKey();
    if (!key) {
      return '';
    }

    const path = `onboarding.steps.${key}.disclaimer`;
    // Check the flat translation table for the key first - translate() logs a "Missing
    // translation" console warning for any key that isn't there, even when the return value goes
    // unused, so calling it unconditionally would warn on every disclaimer-less step.
    const translation = this.translocoService.getTranslation(this.translocoService.getActiveLang());
    return path in translation ? this.translocoService.translate(path) : '';
  });

  protected readonly canGoBack = computed(() => !this.isOnCompletedStep() && this.currentIndex() > 0);

  // True as soon as the communication-preferences step can't be trusted to save - either its
  // initial load already failed (nothing to show or save), or a previous save attempt failed (e.g.
  // HubSpot unreachable in both cases). Checked eagerly, before the user ever has to click "Next"
  // and hit an error, so the wizard doesn't ask them to try something that's already known to fail.
  private readonly communicationPreferencesUnavailable = computed(() => {
    const step = this.communicationPreferencesStep();
    return !!step?.loadError() || (step?.lastSaveFailed() ?? false);
  });

  // Only the invite step is optional. The communication-preferences step becomes skippable too
  // once it's unavailable.
  protected readonly canSkip = computed(
    () =>
      !this.isOnCompletedStep() &&
      (this.currentTaskKey() === 'inviteManager' || (this.currentTaskKey() === 'communicationPreferences' && this.communicationPreferencesUnavailable())),
  );

  // "Next" is replaced by "Skip" for communication-preferences once it's unavailable, so the user
  // is never forced to keep trying.
  protected readonly canShowNext = computed(
    () => this.currentTaskKey() !== 'inviteManager' && !(this.currentTaskKey() === 'communicationPreferences' && this.communicationPreferencesUnavailable()),
  );

  // Steps that own a form gate the primary button on their own validity; steps without a form
  // (or the completed pseudo-step) default to enabled.
  protected readonly canSubmit = computed(() => (this.currentTaskKey() === 'inviteManager' ? (this.inviteManagerStep()?.canSubmit() ?? false) : true));

  protected readonly primaryActionLabel = computed(() => {
    const key = this.isOnCompletedStep() ? 'completed' : this.currentTaskKey();
    return key ? this.translocoService.translate(`onboarding.steps.${key}.action`) : '';
  });

  protected readonly stepperSteps = computed((): readonly OnboardingStepperStep[] => {
    const currentIndex = this.currentIndex();

    const taskSteps: OnboardingStepperStep[] = this.tasks().map((task, index) => ({
      label: this.translocoService.translate(`onboarding.steps.${task.key}.label`),
      labelShort: this.translocoService.translate(`onboarding.steps.${task.key}.labelShort`),
      description: this.translocoService.translate(`onboarding.steps.${task.key}.description`),
      status: index < currentIndex ? 'completed' : index === currentIndex ? 'current' : 'upcoming',
    }));

    return [
      ...taskSteps,
      {
        label: this.translocoService.translate('onboarding.steps.completed.label'),
        labelShort: this.translocoService.translate('onboarding.steps.completed.labelShort'),
        description: this.translocoService.translate('onboarding.steps.completed.description'),
        status: this.isOnCompletedStep() ? 'current' : 'upcoming',
      },
    ];
  });

  protected async onNext(): Promise<void> {
    if (this.isOnCompletedStep()) {
      this.dialogManager.closeAll();
      return;
    }

    const key = this.currentTaskKey();
    if (!key) {
      return;
    }

    if (key === 'communicationPreferences') {
      const saved = await this.communicationPreferencesStep()?.save();
      if (!saved) {
        this.showToast(NS_TOAST_INTENTS.ALERT, 'accountDetails.communicationPreferences.saveErrorTitle', 'accountDetails.communicationPreferences.saveErrorDescription');
        return;
      }

      this.showToast(NS_TOAST_INTENTS.CONFIRM, 'accountDetails.communicationPreferences.saveSuccessTitle', 'accountDetails.communicationPreferences.saveSuccessDescription');
    }

    if (key === 'inviteManager') {
      // The step owns its own failure UI (a field error or a dialog, depending on the failure) -
      // it can tell a duplicate-address 409 apart from a generic failure, the wizard cannot.
      const invited = await this.inviteManagerStep()?.save();
      if (!invited) {
        return;
      }
    }

    await this.completeCurrentTask(key);
  }

  protected async onSkip(): Promise<void> {
    const key = this.currentTaskKey();
    if (key) {
      await this.completeCurrentTask(key);
    }
  }

  protected onPrevious(): void {
    this.currentIndex.update((index) => Math.max(0, index - 1));
  }

  private async completeCurrentTask(key: OnboardingTaskKey): Promise<void> {
    // Re-asserting an already-completed task 409s server-side (only the currently-active task can
    // be completed) - this happens when the user goes back past a completed step and forward again.
    if (this.completedKeys().has(key)) {
      this.currentIndex.update((index) => index + 1);
      return;
    }

    const success = await this.onboardingStatusService.completeTask(key);
    if (!success) {
      this.showToast(NS_TOAST_INTENTS.ALERT, 'onboarding.errors.stepFailedTitle', 'onboarding.errors.stepFailedDescription');
      return;
    }

    this.completedKeys.update((keys) => new Set(keys).add(key));
    this.currentIndex.update((index) => index + 1);
  }

  private showToast(intent: NsToastIntent, headerKey: string, descriptionKey: string): void {
    this.toastManager.show(NsToast, {
      context: {
        intent,
        header: this.translocoService.translate(headerKey),
        description: this.translocoService.translate(descriptionKey),
      },
    });
  }

  private observeElementResize(element: HTMLElement, setHeight: (height: number) => void): ResizeObserver {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHeight(entry.contentRect.height);
      }
    });
    resizeObserver.observe(element);
    return resizeObserver;
  }

  public ngOnDestroy(): void {
    this.stepContentResizeObserver?.disconnect();
  }
}
